/**
 * One row of the F1 inbox table (PROD-3274 DF-04). The words, chips and actions come from `rowView`; this file draws
 * them and runs the row's actions (review, confirm the patient, add, retry). Disregard is lifted to the inbox, which
 * owns the dialog.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Select, StatusChip, TableCell, TableRow } from '@scripsteam/scrips-signal-ds';
import { IntakeApiError } from '../api/intake-client';
import { useAddInboxDocument, useConfirmInboxPatient, useRetryInboxRun } from '../hooks/use-intake-inbox';
import {
  DOC_TYPE_LABEL,
  intakeReviewPath,
  DOC_TYPE_ORDER,
  addLabel,
  fileSubLine,
  inboxActionError,
  isImage,
  isOfferedType,
  rowView,
  typePickerFor,
} from '../lib/inbox-view';
import { isIntakeTypePickerOn } from '../lib/intake-switch';
import type { InboxDocument, IntakeDocType } from '../types';
import { InboxTypePicker } from './inbox-type-picker';

const TYPE_OPTIONS = DOC_TYPE_ORDER.map((value) => ({ value, label: DOC_TYPE_LABEL[value] }));

export interface InboxRowProps {
  doc: InboxDocument;
  patientId: string;
  patientFirstName: string;
  onDisregard: (doc: InboxDocument) => void;
}

function Sub({ children }: { children: React.ReactNode }) {
  return <div data-slot="intake-row-sub">{children}</div>;
}

export function InboxRow({ doc, patientId, patientFirstName, onDisregard }: InboxRowProps) {
  const navigate = useNavigate();
  const view = rowView(doc);
  const [pickedType, setPickedType] = useState<IntakeDocType | null>(null);
  const add = useAddInboxDocument(patientId);
  const confirm = useConfirmInboxPatient(patientId);
  const retry = useRetryInboxRun(patientId);
  // A type the server found outside the scope is not offered: the person picks one before Add.
  const type = pickedType ?? (isOfferedType(doc.docType) ? doc.docType : null);
  // A mutation keeps its error until it runs again, so each action clears the other two when it starts: the line below
  // then always speaks for the action that ran last.
  const mutations = { add, confirm, retry };
  const start = (action: keyof typeof mutations) => {
    for (const [name, m] of Object.entries(mutations)) if (name !== action) m.reset();
  };
  const failedAction = add.isError ? 'add' : confirm.isError ? 'confirm' : retry.isError ? 'retry' : null;
  const failed = failedAction !== null;

  const goReview = () => doc.runId && navigate(intakeReviewPath(patientId, doc.runId));
  const onAdd = () => {
    start('add');
    add.mutate(
      // The server audits a changed type as docTypeSource=User; an unchanged type sends no body.
      { documentId: doc.documentId, docType: type && type !== doc.docType ? type : undefined },
      {
        onError: (e) => {
          // A lab report (picked here, or found by the server) opens review instead of being filed.
          if (e instanceof IntakeApiError && e.status === 409 && e.code === 'review_required') goReview();
        },
      },
    );
  };
  // A 409 review_required is not a failure for the user: review opens.
  // PROD-3339 (?x=intake-type): the type picker with Confirm type on every row that can take a type.
  const typePicker = isIntakeTypePickerOn() ? typePickerFor(doc) : null;
  const showFailure =
    failed && !(add.error instanceof IntakeApiError && add.error.status === 409 && add.error.code === 'review_required');

  return (
    <TableRow className={view.held ? 'bg-status-error-bg' : undefined} data-testid={`inbox-row-${doc.documentId}`}>
      <TableCell>
        <div data-slot="intake-row-file">
          <span aria-hidden="true" data-slot="intake-row-thumb">
            <Icon name={isImage(doc) ? 'phone' : 'file'} size={16} />
          </span>
          <div className="min-w-0">
            <div data-slot="intake-row-name">{doc.fileName}</div>
            <Sub>{fileSubLine(doc)}</Sub>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-sm">
        {typePicker ? (
          <InboxTypePicker
            key={doc.documentId}
            doc={doc}
            patientId={patientId}
            view={typePicker}
            onReview={goReview}
          />
        ) : view.picker ? (
          <Select<string>
            aria-label={`Type of ${doc.fileName}`}
            size="compact"
            options={TYPE_OPTIONS}
            placeholder="Pick a type…"
            value={type ?? undefined}
            onChange={(v) => setPickedType(v)}
          />
        ) : (
          <span className={view.recognized.reading ? 'text-text-secondary' : 'text-text-primary'}>
            {view.recognized.label}
          </span>
        )}
        {view.recognized.sub ? <Sub>{view.recognized.sub}</Sub> : null}
      </TableCell>
      <TableCell>
        {view.identity ? (
          <StatusChip semantic={view.identity.semantic} tone="soft">
            {view.identity.label}
          </StatusChip>
        ) : (
          <span className="text-text-secondary">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {view.goesTo ? (
          <>
            <span className="text-text-primary">{view.goesTo.label}</span>
            {view.goesTo.sub ? <Sub>{view.goesTo.sub}</Sub> : null}
          </>
        ) : (
          <span className="text-text-secondary">—</span>
        )}
      </TableCell>
      <TableCell>
        {view.status ? (
          <StatusChip semantic={view.status.semantic} tone="soft">
            {view.status.label}
          </StatusChip>
        ) : null}
      </TableCell>
      <TableCell>
        <div data-slot="intake-row-actions">
          {view.actions.map((a) => {
            switch (a.kind) {
              case 'review':
                return (
                  <Button key="review" size="sm" variant="primary" onClick={goReview}>
                    Review values
                  </Button>
                );
              case 'confirm':
                return (
                  <Button
                    key="confirm"
                    size="sm"
                    variant="secondary"
                    loading={confirm.isPending}
                    onClick={() => {
                      if (!doc.runId) return;
                      start('confirm');
                      confirm.mutate({ runId: doc.runId });
                    }}
                  >
                    This is {patientFirstName}
                  </Button>
                );
              case 'add':
                return (
                  <Button key="add" size="sm" variant="secondary" loading={add.isPending} disabled={!type} onClick={onAdd}>
                    {addLabel(type)}
                  </Button>
                );
              case 'retry':
                return (
                  <Button
                    key="retry"
                    size="sm"
                    variant="secondary"
                    loading={retry.isPending}
                    onClick={() => {
                      if (!doc.runId) return;
                      start('retry');
                      retry.mutate({ runId: doc.runId });
                    }}
                  >
                    Try again
                  </Button>
                );
              case 'disregard':
                return (
                  <Button key="disregard" size="sm" variant="link" onClick={() => onDisregard(doc)}>
                    Disregard
                  </Button>
                );
              default:
                return (
                  <span key="wait" className="text-xs text-text-secondary">
                    You can leave this page
                  </span>
                );
            }
          })}
        </div>
        {showFailure ? (
          <p role="alert" className="mt-1 text-xs text-status-error">
            {failedAction ? inboxActionError(mutations[failedAction].error, failedAction) : null}
          </p>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
